import { format, parseISO, differenceInDays, isPast, isToday, isFuture } from 'date-fns';
import {
  Calendar,
  Users,
  Clock,
  TrendingUp,
  FolderKanban,
  Building2,
  UserCircle2,
  MapPin,

  Milestone,
  Tag,
  ExternalLink,
  BookOpen,
  CheckCircle2,
  CalendarClock,
  AlertCircle,
  UserX,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useState } from 'react';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DemoProject,
  PROJECT_STATUS_CONFIG,
  PROJECT_TYPE_CONFIG,
  PROJECT_PRIORITY_CONFIG,
  ProjectMilestone,
} from '@/data/mockProjectsData';
import { Booking, Resource } from '@/types';

interface ProjectDetailsModalProps {
  project: DemoProject | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  allBookings?: Booking[];
  resources?: Resource[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

const AVATAR_COLOURS = [
  'bg-violet-500', 'bg-blue-500', 'bg-teal-500', 'bg-emerald-500',
  'bg-amber-500',  'bg-orange-500','bg-rose-500',  'bg-cyan-500',
  'bg-indigo-500', 'bg-fuchsia-500',
];
function avatarColour(name: string) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLOURS[Math.abs(h) % AVATAR_COLOURS.length];
}



// ─── Shared small components ──────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
      {children}
    </p>
  );
}

function InfoRow({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 text-muted-foreground">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="text-xs leading-snug">{children}</span>
    </div>
  );
}

function UtilBar({ label, current, max, pct }: {
  label: string; current: string; max: string; pct: number;
}) {
  const barClass =
    pct >= 100 ? '[&>div]:bg-rose-500' :
    pct >= 90  ? '[&>div]:bg-rose-400' :
    pct >= 75  ? '[&>div]:bg-amber-500' : '';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {current}<span className="mx-1 opacity-40">/</span>{max}
          <span className={`ml-1.5 font-semibold ${pct >= 90 ? 'text-rose-600' : pct >= 75 ? 'text-amber-600' : 'text-foreground'}`}>
            {pct}%
          </span>
        </span>
      </div>
      <Progress value={Math.min(pct, 100)} className={`h-1.5 rounded-full ${barClass}`} />
    </div>
  );
}

// ─── Booking helpers ──────────────────────────────────────────────────────────

function formatBookingDate(startTime: string, endTime: string, durationType: string): string {
  try {
    const start = parseISO(startTime);
    const end   = parseISO(endTime);
    const sameDay = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd');
    if (durationType === 'full-day') {
      return sameDay
        ? format(start, 'dd MMM yyyy')
        : `${format(start, 'dd MMM')} – ${format(end, 'dd MMM yyyy')}`;
    }
    if (durationType === 'hourly') {
      return `${format(start, 'dd MMM yyyy')} · ${format(start, 'HH:mm')}–${format(end, 'HH:mm')}`;
    }
    if (durationType === 'half-day-am') return `${format(start, 'dd MMM yyyy')} · AM`;
    if (durationType === 'half-day-pm') return `${format(start, 'dd MMM yyyy')} · PM`;
    return format(start, 'dd MMM yyyy');
  } catch {
    return '—';
  }
}

const BOOKING_STATUS_STYLE: Record<string, string> = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  planned:   'bg-primary/5 text-primary border-primary/20',
  completed: 'bg-muted text-muted-foreground border-border',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

function BookingRow({ booking }: { booking: Booking }) {
  const isUnresourced =
    booking.resourceId === 'unallocated' ||
    booking.resourceId === null ||
    booking.resourceId === undefined;

  const dateLabel = formatBookingDate(booking.startTime, booking.endTime, booking.durationType);
  const statusStyle = BOOKING_STATUS_STYLE[booking.status] ?? 'bg-muted text-muted-foreground border-border';

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      {/* Colour strip */}
      <div
        className="mt-0.5 h-9 w-1 rounded-full shrink-0"
        style={{ backgroundColor: booking.color || '#94a3b8' }}
      />
      <div className="flex-1 min-w-0 space-y-1">
        {/* Project name + status */}
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold leading-tight truncate">{booking.projectName}</p>
          <span className={`text-[10px] shrink-0 rounded-full border px-2 py-0.5 font-medium capitalize ${statusStyle}`}>
            {booking.status}
          </span>
        </div>
        {/* Resource or unresourced label */}
        {isUnresourced ? (
          <div className="flex items-center gap-1 text-[11px] text-amber-600 font-medium">
            <AlertCircle className="h-3 w-3 shrink-0" />
            <span>Unresourced{booking.requiredRole ? ` · ${booking.requiredRole}` : ''}</span>
          </div>
        ) : (
          <p className="text-[11px] text-muted-foreground truncate">
            {booking.requiredRole || 'Resource assigned'}
          </p>
        )}
        {/* Date + duration type */}
        <p className="text-[11px] text-muted-foreground tabular-nums">{dateLabel}</p>
      </div>
    </div>
  );
}

interface BookingSectionProps {
  icon: React.ElementType;
  title: string;
  count: number;
  bookings: Booking[];
  emptyText: string;
  defaultOpen: boolean;
  accentClass: string;
  badge?: React.ReactNode;
}

function BookingSection({
  icon: Icon,
  title,
  count,
  bookings,
  emptyText,
  defaultOpen,
  accentClass,
  badge,
}: BookingSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className={`h-6 w-6 rounded-lg bg-muted flex items-center justify-center`}>
                <Icon className={`h-3.5 w-3.5 ${accentClass}`} />
              </div>
              <span className="text-sm font-semibold">{title}</span>
              {badge}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium tabular-nums ${accentClass}`}>
                {count}
              </span>
              {open
                ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              }
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="border-t border-border/50">
            {count === 0 ? (
              <p className="text-xs text-muted-foreground px-4 py-4 text-center">{emptyText}</p>
            ) : (
              <div className="px-4">
                {bookings
                  .slice()
                  .sort((a, b) => a.startTime.localeCompare(b.startTime))
                  .map((b) => (
                    <BookingRow key={b.id} booking={b} />
                  ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function ProjectDetailsModal({ project, open, onOpenChange, allBookings = [], resources = [] }: ProjectDetailsModalProps) {
  if (!project) return null;

  const statusCfg   = PROJECT_STATUS_CONFIG[project.status];
  const typeCfg     = PROJECT_TYPE_CONFIG[project.type];
  const priorityCfg = PROJECT_PRIORITY_CONFIG[project.priority];

  const startDate    = parseISO(project.startDate);
  const endDate      = parseISO(project.endDate);
  const totalDays    = differenceInDays(endDate, startDate) || 1;
  const elapsed      = differenceInDays(new Date(), startDate);
  const timeProgress = Math.min(100, Math.max(0, Math.round((elapsed / totalDays) * 100)));
  const daysLeft     = differenceInDays(endDate, new Date());

  const hoursProgress = project.budgetedHours > 0
    ? Math.round((project.actualHours / project.budgetedHours) * 100) : 0;

  const totalBudgetCost  = project.budgetItems.reduce((s, i) => s + i.budgetedHours * i.costRate, 0);
  const totalActualCost  = project.budgetItems.reduce((s, i) => s + i.actualHours  * i.costRate, 0);
  const totalBilledValue = project.budgetItems.reduce((s, i) => s + i.actualHours  * i.billingRate, 0);
  const budgetPct        = totalBudgetCost > 0 ? Math.round((totalActualCost / totalBudgetCost) * 100) : 0;

  const daysLabel =
    daysLeft > 0   ? `${daysLeft}d remaining` :
    daysLeft === 0 ? 'Due today' :
                     `${Math.abs(daysLeft)}d overdue`;

  // ── Booking classification ──────────────────────────────────────────────────
  // Match bookings to this project by projectName (projectId uses a different
  // ID system in the mock data — see mock-data.ts 'pd1' vs DemoProject 'proj-001')
  const projectBookings = allBookings.filter(
    (b) =>
      b.projectName?.toLowerCase() === project.name?.toLowerCase() &&
      b.status !== 'cancelled',
  );

  const completedBookings = projectBookings.filter((b) => {
    if (b.status === 'completed') return true;
    const end = parseISO(b.endTime);
    return isPast(end) && !isToday(end);
  });

  const unresourcedBookings = projectBookings.filter((b) => {
    const end = parseISO(b.endTime);
    const isNotPast = isFuture(end) || isToday(end);
    return (
      isNotPast &&
      b.status !== 'completed' &&
      (b.resourceId === 'unallocated' ||
        b.resourceId === null ||
        b.resourceId === undefined ||
        (b.resourceIds?.length === 1 && b.resourceIds[0] === 'unallocated'))
    );
  });

  const plannedBookings = projectBookings.filter((b) => {
    const end = parseISO(b.endTime);
    const isNotPast = isFuture(end) || isToday(end);
    return (
      isNotPast &&
      b.status !== 'completed' &&
      b.resourceId !== 'unallocated' &&
      b.resourceId !== null &&
      b.resourceId !== undefined &&
      !(b.resourceIds?.length === 1 && b.resourceIds[0] === 'unallocated')
    );
  });

  // ── Build team from bookings + resources ─────────────────────────────────────
  // Derive team members from bookings assigned to this project. Each unique
  // resource that has at least one booking on this project becomes a team member.
  // Matches EBProjects Team tab columns: Name, Role, Allocated Hrs, Actual Hrs,
  // Remaining, Status, Booking Status.
  const resourceLookup: Record<string, Resource> = {};
  resources.forEach((r) => { if (r.id) resourceLookup[r.id] = r; });

  interface DerivedTeamMember {
    id: string;
    name: string;
    role: string;
    department: string;
    email: string;
    avatarUrl?: string;
    allocatedHours: number;
    actualHours: number;
    remainingHours: number;
    status: 'On Track' | 'At Risk' | 'Over Allocated';
    bookingStatus: 'Confirmed' | 'Planned' | 'No Booking';
    bookingCount: number;
  }

  const derivedTeamMembers: DerivedTeamMember[] = (() => {
    // Build team from bookings
    const memberMap: Record<string, {
      id: string; name: string; role: string; department: string;
      email: string; avatarUrl?: string; allocatedHours: number;
      actualHours: number; bookingCount: number;
      hasConfirmed: boolean; hasPlanned: boolean;
    }> = {};

    projectBookings.forEach((b) => {
      const resIds = b.resourceIds?.length
        ? b.resourceIds
        : b.resourceId ? [b.resourceId] : [];
      resIds.forEach((rid) => {
        if (!rid || rid === 'unallocated') return;
        if (!memberMap[rid]) {
          const res = resourceLookup[rid];
          memberMap[rid] = {
            id: rid,
            name: res?.name ?? rid,
            role: res?.role ?? b.requiredRole ?? '',
            department: res?.department ?? '',
            email: res?.email ?? '',
            avatarUrl: res?.photoUrl,
            allocatedHours: 0,
            actualHours: 0,
            bookingCount: 0,
            hasConfirmed: false,
            hasPlanned: false,
          };
        }
        // Estimate hours from booking duration
        const bStart = parseISO(b.startTime);
        const bEnd = parseISO(b.endTime);
        const diffMs = bEnd.getTime() - bStart.getTime();
        const hours = b.durationType === 'full-day'
          ? Math.max(1, differenceInDays(bEnd, bStart)) * 8
          : b.durationType === 'half-day-am' || b.durationType === 'half-day-pm'
            ? 4
            : Math.max(0, diffMs / (1000 * 60 * 60));
        memberMap[rid].allocatedHours += hours;
        memberMap[rid].bookingCount += 1;

        // Track booking status
        if (b.status === 'confirmed' || b.status === 'completed') {
          memberMap[rid].hasConfirmed = true;
        } else if (b.status === 'planned') {
          memberMap[rid].hasPlanned = true;
        }

        // Count completed bookings as actual hours
        if (b.status === 'completed' || (isPast(bEnd) && !isToday(bEnd))) {
          memberMap[rid].actualHours += hours;
        }
      });
    });

    // Add project manager if not already in the map
    if (project.projectManagerId && !memberMap[project.projectManagerId]) {
      const pmRes = resourceLookup[project.projectManagerId];
      if (pmRes) {
        memberMap[project.projectManagerId] = {
          id: project.projectManagerId,
          name: pmRes.name ?? project.projectManagerName,
          role: pmRes.role ?? 'Project Manager',
          department: pmRes.department ?? '',
          email: pmRes.email ?? '',
          avatarUrl: pmRes.photoUrl,
          allocatedHours: 0,
          actualHours: 0,
          bookingCount: 0,
          hasConfirmed: false,
          hasPlanned: false,
        };
      }
    }

    return Object.values(memberMap)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((m) => {
        const remaining = Math.max(0, m.allocatedHours - m.actualHours);
        const utilisationPct = m.allocatedHours > 0 ? (m.actualHours / m.allocatedHours) * 100 : 0;
        const status: DerivedTeamMember['status'] =
          utilisationPct > 100 ? 'Over Allocated' :
          utilisationPct > 80  ? 'At Risk' : 'On Track';
        const bookingStatus: DerivedTeamMember['bookingStatus'] =
          m.hasConfirmed ? 'Confirmed' :
          m.hasPlanned   ? 'Planned' : 'No Booking';
        return {
          id: m.id,
          name: m.name,
          role: m.role,
          department: m.department,
          email: m.email,
          avatarUrl: m.avatarUrl,
          allocatedHours: Math.round(m.allocatedHours),
          actualHours: Math.round(m.actualHours),
          remainingHours: Math.round(remaining),
          status,
          bookingStatus,
          bookingCount: m.bookingCount,
        };
      });
  })();

  // ── Budget summary from EBProjects fields ──────────────────────────────────
  // Use project-level budget values for summary cards when budgetItems has
  // the EBProjects Labour/Expense/Revenue breakdown (costRate stores budget
  // amounts, billingRate stores actual amounts for expense/revenue rows).
  const hasEBBudgetBreakdown = project.budgetItems.some(
    (i) => i.id.endsWith('-labour') || i.id.endsWith('-expense') || i.id.endsWith('-revenue'),
  );
  const budgetSummaryTotal  = hasEBBudgetBreakdown ? project.budgetedValue : totalBudgetCost;
  const actualSummaryTotal  = hasEBBudgetBreakdown ? project.actualCost : totalActualCost;
  const billedSummaryTotal  = hasEBBudgetBreakdown ? project.actualCost : totalBilledValue;
  const budgetSummaryPct    = budgetSummaryTotal > 0 ? Math.round((actualSummaryTotal / budgetSummaryTotal) * 100) : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[620px] p-0 flex flex-col overflow-hidden bg-background"
      >

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
              <FolderKanban className="h-4 w-4 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-mono font-medium text-muted-foreground tracking-widest truncate leading-none mb-1">
                {project.code}
              </p>
              <p className="text-sm font-semibold leading-tight truncate">{project.name}</p>
            </div>
          </div>
          <Badge
            variant="outline"
            accent={statusCfg.accent as any}
            className="shrink-0 text-xs px-2.5 py-0.5 rounded-full"
          >
            <span className="flex items-center gap-1.5">
              <span className={`h-1.5 w-1.5 rounded-full ${statusCfg.dotClass} shrink-0`} />
              {statusCfg.label}
            </span>
          </Badge>
        </div>

        {/* ── Identity strip ──────────────────────────────────────────────── */}
        <div className="px-5 py-4 shrink-0 space-y-3.5">

          {/* Meta */}
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            {project.customerName && (
              <InfoRow icon={Building2}>
                {project.customerCode && (
                  <span className="font-mono text-foreground mr-1">{project.customerCode}</span>
                )}
                <span className="truncate">{project.customerName}</span>
              </InfoRow>
            )}
            <InfoRow icon={UserCircle2}>{project.projectManagerName}</InfoRow>
            {project.region && <InfoRow icon={MapPin}>{project.region}</InfoRow>}
            <InfoRow icon={Calendar}>
              <span className={
                daysLeft < 0 ? 'text-rose-500 font-medium' :
                daysLeft <= 7 ? 'text-amber-500 font-medium' :
                'text-muted-foreground'
              }>
                {format(endDate, 'dd MMM yyyy')}
                <span className="opacity-60 ml-1.5 font-normal">({daysLabel})</span>
              </span>
            </InfoRow>
          </div>

          {/* Progress */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Completion</span>
              <span className="font-semibold tabular-nums">{project.percentComplete}%</span>
            </div>
            <Progress value={project.percentComplete} className="h-1 rounded-full" />
          </div>

          {/* Pills + link */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="text-[11px] font-normal rounded-full px-2.5 py-0.5 border border-border text-muted-foreground bg-muted/40">
                {typeCfg.label}
              </span>
              <span className="text-[11px] font-normal rounded-full px-2.5 py-0.5 border border-border text-muted-foreground bg-muted/40">
                {priorityCfg.label}
              </span>
              {project.template && (
                <span className="text-[11px] font-mono rounded-full px-2.5 py-0.5 border border-border text-muted-foreground bg-muted/40">
                  {project.template}
                </span>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-xs gap-1.5 text-muted-foreground hover:text-foreground shrink-0" asChild>
              <a href={project.projectUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-3 w-3" />
                Open
              </a>
            </Button>
          </div>
        </div>

        {/* ── Tabs ────────────────────────────────────────────────────────── */}
        <Tabs defaultValue="overview" className="flex flex-col flex-1 min-h-0 overflow-hidden">

          <div className="px-4 pt-0 pb-0 shrink-0">
            <TabsList variant="default" className="w-full h-9 flex">
              <TabsTrigger value="overview" variant="default" className="flex-1 text-xs px-2">Overview</TabsTrigger>
              <TabsTrigger value="team"     variant="default" className="flex-1 text-xs px-2">Team</TabsTrigger>
              <TabsTrigger value="timeline" variant="default" className="flex-1 text-xs px-2">Timeline</TabsTrigger>
              <TabsTrigger value="bookings" variant="default" className="flex-1 text-xs px-2"
                badge={unresourcedBookings.length > 0 ? unresourcedBookings.length : undefined}
                badgeVariant="default"
              >Bookings</TabsTrigger>
              <TabsTrigger value="budget"   variant="default" className="flex-1 text-xs px-2">Budget</TabsTrigger>
              <TabsTrigger value="time"     variant="default" className="flex-1 text-xs px-2">Time</TabsTrigger>
            </TabsList>
          </div>

          {/* ── OVERVIEW ──────────────────────────────────────────────────── */}
          <TabsContent value="overview" className="flex-1 overflow-y-auto min-h-0 p-5 space-y-5">

            {project.description && (
              <div>
                <SectionLabel>About</SectionLabel>
                <p className="text-sm text-muted-foreground leading-relaxed">{project.description}</p>
              </div>
            )}



            {/* At a glance */}
            <div>
              <SectionLabel>At a Glance</SectionLabel>
              <div className="grid grid-cols-2 gap-2.5">
                {[
                  {
                    label: 'Budgeted Hours',
                    value: `${project.budgetedHours.toLocaleString()}h`,
                    sub: `${project.actualHours.toLocaleString()}h logged`,
                    icon: Clock,
                  },
                  {
                    label: 'Time Elapsed',
                    value: `${timeProgress}%`,
                    sub: daysLabel,
                    icon: TrendingUp,
                  },
                  {
                    label: 'Team Size',
                    value: String(derivedTeamMembers.length),
                    sub: `${new Set(derivedTeamMembers.map((t) => t.department).filter(Boolean)).size || 0} dept${(new Set(derivedTeamMembers.map((t) => t.department).filter(Boolean)).size || 0) !== 1 ? 's' : ''}`,
                    icon: Users,
                  },
                  {
                    label: project.milestones.length > 0 ? 'Milestones' : 'Bookings',
                    value: project.milestones.length > 0 ? String(project.milestones.length) : String(projectBookings.length),
                    sub: project.milestones.length > 0
                      ? `${project.milestones.filter((m) => m.status === 'completed').length} completed`
                      : `${unresourcedBookings.length} unresourced`,
                    icon: project.milestones.length > 0 ? Milestone : BookOpen,
                  },
                ].map(({ label, value, sub, icon: Icon }) => (
                  <div key={label} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
                      <Icon className="h-3.5 w-3.5 text-muted-foreground/30" />
                    </div>
                    <p className="text-xl font-semibold tracking-tight">{value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
                  </div>
                ))}
              </div>
            </div>



            {/* Utilisation */}
            <div>
              <SectionLabel>Utilisation</SectionLabel>
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <UtilBar
                  label="Hours logged"
                  current={`${project.actualHours.toLocaleString()}h`}
                  max={`${project.budgetedHours.toLocaleString()}h`}
                  pct={hoursProgress}
                />
                <UtilBar
                  label="Budget cost"
                  current={`£${Math.round(totalActualCost).toLocaleString()}`}
                  max={`£${Math.round(totalBudgetCost).toLocaleString()}`}
                  pct={budgetPct}
                />
              </div>
            </div>

            {/* Tags */}
            {project.tags.length > 0 && (
              <>
    
                <div>
                  <SectionLabel>Tags</SectionLabel>
                  <div className="flex flex-wrap gap-1.5">
                    {project.tags.map((tag) => (
                      <div key={tag} className="flex items-center gap-1 text-xs text-muted-foreground bg-muted rounded-full px-2.5 py-1">
                        <Tag className="h-3 w-3 opacity-50" />
                        {tag}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </TabsContent>

          {/* ── TEAM (EBProjects layout) ────────────────────────────────── */}
          <TabsContent value="team" className="flex-1 overflow-y-auto min-h-0 p-5 space-y-4">
            {derivedTeamMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Users className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <p className="text-sm text-muted-foreground">No team members assigned yet.</p>
                <p className="text-[11px] text-muted-foreground/70">Resources booked against this project will appear here.</p>
              </div>
            ) : (
              <>
                {/* Header */}
                <div>
                  <p className="text-sm font-semibold">Team Management</p>
                  <p className="text-xs text-muted-foreground">Comprehensive team member tracking and allocation</p>
                </div>

                {/* Table */}
                <div className="rounded-xl border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-b border-border bg-muted/30">
                        <TableHead className="text-[10px] uppercase tracking-wider pl-4 text-muted-foreground font-semibold h-8">Name</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Role</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Allocated Hrs</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Actual Hrs</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Remaining</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Status</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider pr-4 text-muted-foreground font-semibold h-8">Booking Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {derivedTeamMembers.map((member) => {
                        const colour = avatarColour(member.name);
                        const statusClass =
                          member.status === 'On Track'       ? 'text-emerald-600' :
                          member.status === 'At Risk'        ? 'text-amber-600' :
                                                               'text-rose-600';
                        const bookingStatusClass =
                          member.bookingStatus === 'Confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                          member.bookingStatus === 'Planned'   ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                                                  'bg-amber-50 text-amber-700 border-amber-200';
                        const bookingIcon =
                          member.bookingStatus === 'Confirmed' ? <CheckCircle2 className="h-3 w-3 mr-1 inline" /> :
                          member.bookingStatus === 'No Booking' ? <AlertCircle className="h-3 w-3 mr-1 inline" /> :
                                                                   null;
                        return (
                          <TableRow key={member.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                            <TableCell className="py-2.5 pl-4">
                              <div className="flex items-center gap-2.5">
                                <Avatar className="h-7 w-7 shrink-0">
                                  <AvatarImage src={member.avatarUrl} alt={member.name} className="object-cover" />
                                  <AvatarFallback className={`${colour} text-white font-semibold text-[10px]`}>
                                    {getInitials(member.name)}
                                  </AvatarFallback>
                                </Avatar>
                                <span className="text-xs font-medium truncate">{member.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground py-2.5 whitespace-nowrap">{member.role}</TableCell>
                            <TableCell className="text-xs py-2.5 text-right tabular-nums font-medium">
                              {member.allocatedHours > 0 ? `${member.allocatedHours}h` : <span className="text-muted-foreground/60 italic">Not allocated</span>}
                            </TableCell>
                            <TableCell className="text-xs py-2.5 text-right tabular-nums">{member.actualHours}h</TableCell>
                            <TableCell className="text-xs py-2.5 text-right tabular-nums">{member.remainingHours}h</TableCell>
                            <TableCell className="py-2.5">
                              <span className={`text-xs font-semibold ${statusClass}`}>{member.status}</span>
                            </TableCell>
                            <TableCell className="py-2.5 pr-4">
                              <span className={`text-[10px] rounded-full border px-2 py-0.5 font-medium inline-flex items-center ${bookingStatusClass}`}>
                                {bookingIcon}
                                {member.bookingStatus}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                {/* Summary row */}
                <div className="text-[11px] text-muted-foreground text-center">
                  {derivedTeamMembers.length} member{derivedTeamMembers.length !== 1 ? 's' : ''}
                  {' · '}
                  {derivedTeamMembers.filter((m) => m.bookingStatus === 'Confirmed').length} confirmed
                  {' · '}
                  {derivedTeamMembers.filter((m) => m.bookingStatus === 'No Booking').length} without bookings
                </div>
              </>
            )}
          </TabsContent>

          {/* ── TIMELINE ────────────────────────────────────────────────────── */}
          <TabsContent value="timeline" className="flex-1 overflow-y-auto min-h-0 p-5 space-y-5">

            {/* Date range card */}
            <div>
              <SectionLabel>Date Range</SectionLabel>
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Start</p>
                    <p className="text-sm font-semibold">{format(startDate, 'dd MMM yyyy')}</p>
                  </div>
                  <div className="flex-1 mx-4 flex items-center gap-1.5">
                    <div className="h-px flex-1 bg-border" />
                    <div className="h-1.5 w-1.5 rounded-full bg-primary/50" />
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">End</p>
                    <p className={`text-sm font-semibold ${daysLeft < 0 ? 'text-rose-500' : daysLeft <= 7 ? 'text-amber-500' : ''}`}>
                      {format(endDate, 'dd MMM yyyy')}
                    </p>
                  </div>
                </div>
                <UtilBar
                  label="Time elapsed"
                  current={`${Math.max(0, elapsed)}d`}
                  max={`${totalDays}d`}
                  pct={timeProgress}
                />
              </div>
            </div>



            {/* Milestones */}
            <div>
              <SectionLabel>
                {project.milestones.length} Milestone{project.milestones.length !== 1 ? 's' : ''}
              </SectionLabel>
              {project.milestones.length === 0 ? (
                <p className="text-sm text-muted-foreground">No milestones defined.</p>
              ) : (
                <div className="relative pl-5">
                  {/* Vertical thread line */}
                  <div className="absolute left-[7px] top-2 bottom-2 w-px bg-border/60" />
                  <div className="space-y-4">
                    {project.milestones.map((ms) => {
                      const isComplete = ms.status === 'completed';
                      const isOverdue  = ms.status === 'overdue';
                      const isActive   = ms.status === 'in-progress';
                      const dotClass   = isComplete ? 'bg-emerald-500'
                                       : isOverdue  ? 'bg-rose-500'
                                       : isActive   ? 'bg-primary'
                                       :              'bg-border';
                      const pillClass  = isComplete ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                       : isOverdue  ? 'bg-rose-50 text-rose-700 border-rose-200'
                                       : isActive   ? 'bg-primary/10 text-primary border-primary/20'
                                       :              'bg-muted text-muted-foreground border-border';
                      const pillLabel  = isComplete ? 'Done'
                                       : isOverdue  ? 'Overdue'
                                       : isActive   ? 'Active'
                                       :              'Pending';
                      return (
                        <div key={ms.id} className="relative flex gap-3">
                          {/* Status dot */}
                          <div className={`absolute -left-5 top-[5px] h-2.5 w-2.5 rounded-full border-2 border-background ring-1 ring-border ${dotClass}`} />
                          {/* Content */}
                          <div className="flex-1 min-w-0 rounded-lg border border-border bg-card px-3 py-2.5">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-sm font-medium leading-tight truncate">{ms.name}</p>
                              <span className={`text-[10px] shrink-0 rounded-full border px-2 py-0.5 font-medium ${pillClass}`}>
                                {pillLabel}
                              </span>
                            </div>
                            {ms.description && (
                              <p className="text-xs text-muted-foreground leading-snug mb-2">{ms.description}</p>
                            )}
                            <p className="text-[11px] text-muted-foreground tabular-nums">
                              {ms.completedDate
                                ? `Completed ${format(parseISO(ms.completedDate), 'dd MMM yyyy')}`
                                : `Due ${format(parseISO(ms.dueDate), 'dd MMM yyyy')}`}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ── BUDGET ──────────────────────────────────────────────────────── */}
          {/* ── BOOKINGS ──────────────────────────────────────────────────────── */}
          <TabsContent value="bookings" className="flex-1 overflow-y-auto min-h-0 p-5 space-y-4">

            {projectBookings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <div>
                  <p className="text-sm font-medium">No bookings found</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Bookings raised against this project will appear here.
                  </p>
                </div>
              </div>
            ) : (
              <>
                {/* ── Summary strip ──────────────────────────────────────────── */}
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Completed',    value: completedBookings.length,   colour: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
                    { label: 'Planned',      value: plannedBookings.length,     colour: 'text-primary',     bg: 'bg-primary/5 border-primary/10' },
                    { label: 'Unresourced',  value: unresourcedBookings.length, colour: unresourcedBookings.length > 0 ? 'text-amber-600' : 'text-muted-foreground', bg: unresourcedBookings.length > 0 ? 'bg-amber-50 border-amber-100' : 'bg-muted/30 border-border' },
                  ].map(({ label, value, colour, bg }) => (
                    <div key={label} className={`rounded-xl border ${bg} p-3 text-center`}>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
                      <p className={`text-xl font-semibold tabular-nums ${colour}`}>{value}</p>
                    </div>
                  ))}
                </div>

                {/* ── Planned bookings ───────────────────────────────────────── */}
                <BookingSection
                  icon={CalendarClock}
                  title="Planned"
                  count={plannedBookings.length}
                  bookings={plannedBookings}
                  emptyText="No planned bookings for this project."
                  defaultOpen={true}
                  accentClass="text-primary"
                />

                {/* ── Unresourced bookings ───────────────────────────────────── */}
                <BookingSection
                  icon={UserX}
                  title="Unresourced"
                  count={unresourcedBookings.length}
                  bookings={unresourcedBookings}
                  emptyText="No unresourced bookings — all demand is covered."
                  defaultOpen={unresourcedBookings.length > 0}
                  accentClass={unresourcedBookings.length > 0 ? 'text-amber-600' : 'text-muted-foreground'}
                  badge={unresourcedBookings.length > 0 ? (
                    <span className="text-[10px] font-medium rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200">
                      Needs resourcing
                    </span>
                  ) : undefined}
                />

                {/* ── Completed bookings ─────────────────────────────────────── */}
                <BookingSection
                  icon={CheckCircle2}
                  title="Completed"
                  count={completedBookings.length}
                  bookings={completedBookings}
                  emptyText="No completed bookings yet."
                  defaultOpen={false}
                  accentClass="text-emerald-600"
                />
              </>
            )}
          </TabsContent>

          <TabsContent value="budget" className="flex-1 overflow-y-auto min-h-0 p-5 space-y-5">

            {/* Summary trio */}
            <div>
              <SectionLabel>Summary</SectionLabel>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Budget', value: `£${(budgetSummaryTotal  / 1000).toFixed(1)}k`, warn: false },
                  { label: 'Actual', value: `£${(actualSummaryTotal  / 1000).toFixed(1)}k`, warn: actualSummaryTotal > budgetSummaryTotal },
                  { label: hasEBBudgetBreakdown ? 'Variance' : 'Billed', value: hasEBBudgetBreakdown ? `£${((budgetSummaryTotal - actualSummaryTotal) / 1000).toFixed(1)}k` : `£${(billedSummaryTotal / 1000).toFixed(1)}k`, warn: hasEBBudgetBreakdown && actualSummaryTotal > budgetSummaryTotal },
                ].map(({ label, value, warn }) => (
                  <div key={label} className="rounded-xl border border-border bg-card p-3.5 text-center">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">{label}</p>
                    <p className={`text-lg font-semibold tabular-nums ${warn ? 'text-rose-600' : ''}`}>{value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Cost burn */}
            <div className="rounded-xl border border-border bg-card p-4">
              <UtilBar
                label="Cost burn"
                current={`£${Math.round(actualSummaryTotal).toLocaleString()}`}
                max={`£${Math.round(budgetSummaryTotal).toLocaleString()}`}
                pct={budgetSummaryPct}
              />
            </div>

            {/* Budget breakdown table */}
            {project.budgetItems.length > 0 && (
              <div>
                <SectionLabel>{hasEBBudgetBreakdown ? 'Budget Breakdown' : 'Resource Hours'}</SectionLabel>
                <div className="rounded-xl border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-b border-border bg-muted/30">
                        <TableHead className="text-[10px] uppercase tracking-wider pl-4 text-muted-foreground font-semibold h-8">{hasEBBudgetBreakdown ? 'Category' : 'Resource'}</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Budget</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Actual</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right pr-4 text-muted-foreground font-semibold h-8">{hasEBBudgetBreakdown ? 'Variance' : 'Left'}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {project.budgetItems.map((item) => {
                        const isEBRow = item.id.endsWith('-labour') || item.id.endsWith('-expense') || item.id.endsWith('-revenue');
                        if (isEBRow && item.id.endsWith('-labour')) {
                          // Labour row: show hours
                          const pct = item.budgetedHours > 0 ? Math.round((item.actualHours / item.budgetedHours) * 100) : 0;
                          const over = pct > 90;
                          return (
                            <TableRow key={item.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                              <TableCell className="py-2.5 pl-4">
                                <p className="text-xs font-medium leading-tight">{item.resource}</p>
                                <p className="text-[10px] text-muted-foreground">{item.role}</p>
                              </TableCell>
                              <TableCell className="text-xs text-right tabular-nums text-muted-foreground">
                                £{Math.round(item.costRate * item.budgetedHours).toLocaleString()}
                                <p className="text-[10px]">{item.budgetedHours}h</p>
                              </TableCell>
                              <TableCell className="text-xs text-right tabular-nums">
                                <span className={over ? 'text-rose-600 font-semibold' : ''}>
                                  £{Math.round(item.costRate * item.actualHours).toLocaleString()}
                                </span>
                                <p className="text-[10px] text-muted-foreground">{item.actualHours}h ({pct}%)</p>
                              </TableCell>
                              <TableCell className="text-xs text-right tabular-nums text-muted-foreground pr-4">
                                £{Math.round(item.costRate * item.remainingHours).toLocaleString()}
                                <p className="text-[10px]">{item.remainingHours}h</p>
                              </TableCell>
                            </TableRow>
                          );
                        }
                        if (isEBRow && (item.id.endsWith('-expense') || item.id.endsWith('-revenue'))) {
                          // Expense/Revenue rows: costRate=budget amount, billingRate=actual amount
                          const budgetAmt = item.costRate;
                          const actualAmt = item.billingRate;
                          const varianceAmt = budgetAmt - actualAmt;
                          const over = budgetAmt > 0 && actualAmt > budgetAmt;
                          return (
                            <TableRow key={item.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                              <TableCell className="py-2.5 pl-4">
                                <p className="text-xs font-medium leading-tight">{item.resource}</p>
                                <p className="text-[10px] text-muted-foreground">{item.role}</p>
                              </TableCell>
                              <TableCell className="text-xs text-right tabular-nums text-muted-foreground">£{Math.round(budgetAmt).toLocaleString()}</TableCell>
                              <TableCell className="text-xs text-right tabular-nums">
                                <span className={over ? 'text-rose-600 font-semibold' : ''}>£{Math.round(actualAmt).toLocaleString()}</span>
                              </TableCell>
                              <TableCell className={`text-xs text-right tabular-nums pr-4 ${varianceAmt < 0 ? 'text-rose-600' : 'text-muted-foreground'}`}>
                                £{Math.round(varianceAmt).toLocaleString()}
                              </TableCell>
                            </TableRow>
                          );
                        }
                        // Standard (non-EB) budget items: show hours as before
                        const pct = item.budgetedHours > 0 ? Math.round((item.actualHours / item.budgetedHours) * 100) : 0;
                        const over = pct > 90;
                        return (
                          <TableRow key={item.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                            <TableCell className="py-2.5 pl-4">
                              <p className="text-xs font-medium leading-tight">{item.resource}</p>
                              <p className="text-[10px] text-muted-foreground">{item.role}</p>
                            </TableCell>
                            <TableCell className="text-xs text-right tabular-nums text-muted-foreground">{item.budgetedHours}h</TableCell>
                            <TableCell className="text-xs text-right tabular-nums">
                              <span className={over ? 'text-rose-600 font-semibold' : ''}>{item.actualHours}h</span>
                              <p className="text-[10px] text-muted-foreground">{pct}%</p>
                            </TableCell>
                            <TableCell className="text-xs text-right tabular-nums text-muted-foreground pr-4">{item.remainingHours}h</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </TabsContent>

          {/* ── TIME ENTRY ────────────────────────────────────────────────────── */}
          <TabsContent value="time" className="flex-1 overflow-y-auto min-h-0 p-5 space-y-5">
            {project.timeEntries.length === 0 && project.actualHours === 0 && project.budgetedHours === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Clock className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <div>
                  <p className="text-sm font-medium">No time data available</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Time logged against this project will appear here.</p>
                </div>
              </div>
            ) : project.timeEntries.length > 0 ? (
              <>
                {/* Summary trio from individual entries */}
                <div>
                  <SectionLabel>Summary</SectionLabel>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Entries',   value: String(project.timeEntries.length) },
                      { label: 'Total hrs', value: `${project.timeEntries.reduce((s, e) => s + e.hours, 0)}h` },
                      { label: 'Approved',  value: String(project.timeEntries.filter((e) => e.status === 'approved').length) },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-xl border border-border bg-card p-3.5 text-center">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">{label}</p>
                        <p className="text-lg font-semibold tabular-nums">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Entries */}
                <div>
                  <SectionLabel>Entries</SectionLabel>
                  <div className="rounded-xl border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent border-b border-border bg-muted/30">
                          <TableHead className="text-[10px] uppercase tracking-wider pl-4 text-muted-foreground font-semibold h-8">Date</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Resource</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Note</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Hrs</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider pr-4 text-muted-foreground font-semibold h-8">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {project.timeEntries.map((entry) => {
                          const approved = entry.status === 'approved';
                          const rejected = entry.status === 'rejected';
                          return (
                            <TableRow key={entry.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                              <TableCell className="text-[11px] py-2.5 pl-4 whitespace-nowrap font-mono text-muted-foreground">
                                {format(parseISO(entry.date), 'dd MMM')}
                              </TableCell>
                              <TableCell className="text-xs py-2.5 whitespace-nowrap font-medium">{entry.resource}</TableCell>
                              <TableCell className="text-[11px] py-2.5 max-w-[80px] truncate text-muted-foreground">{entry.description}</TableCell>
                              <TableCell className="text-xs py-2.5 text-right font-semibold tabular-nums">{entry.hours}h</TableCell>
                              <TableCell className="py-2.5 pr-4">
                                <span className={`
                                  text-[10px] rounded-full border px-2 py-0.5 font-medium inline-block
                                  ${approved ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                    rejected  ? 'bg-rose-50 text-rose-700 border-rose-200' :
                                               'bg-amber-50 text-amber-700 border-amber-200'}
                                `}>
                                  {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Time summary from EBProjects API (no individual entries available) */}
                <div>
                  <SectionLabel>Hours Summary</SectionLabel>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { label: 'Budgeted', value: `${project.budgetedHours.toLocaleString()}h` },
                      { label: 'Actual',   value: `${project.actualHours.toLocaleString()}h` },
                      { label: 'Remaining', value: `${Math.max(0, project.budgetedHours - project.actualHours).toLocaleString()}h` },
                    ].map(({ label, value }) => (
                      <div key={label} className="rounded-xl border border-border bg-card p-3.5 text-center">
                        <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1.5">{label}</p>
                        <p className="text-lg font-semibold tabular-nums">{value}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hours utilisation bar */}
                <div className="rounded-xl border border-border bg-card p-4">
                  <UtilBar
                    label="Hours utilisation"
                    current={`${project.actualHours.toLocaleString()}h`}
                    max={`${project.budgetedHours.toLocaleString()}h`}
                    pct={hoursProgress}
                  />
                </div>

                {/* Completion */}
                {project.percentComplete > 0 && (
                  <div className="rounded-xl border border-border bg-card p-4">
                    <UtilBar
                      label="Project completion"
                      current={`${project.percentComplete}%`}
                      max="100%"
                      pct={project.percentComplete}
                    />
                  </div>
                )}

                <p className="text-[11px] text-muted-foreground/60 text-center">
                  Summary from EBProjects. Individual time entries are managed in the Projects application.
                </p>
              </>
            )}
          </TabsContent>

        </Tabs>
      </SheetContent>
    </Sheet>
  );
}
